import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { Bullet } from './Bullet';

export class Ship implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;
  public radius: number = 8;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private angle: number = 0; // Facing upward initially
  private isThrusting: boolean = false;
  private shootCooldown: number = 0;
  private gameWidth: number;
  private gameHeight: number;

  // Physics constants
  private readonly THRUST_POWER: number = 300;
  private readonly MAX_SPEED: number = 400;
  private readonly ROTATION_SPEED: number = 300; // degrees per second
  private readonly FRICTION: number = 0.98;
  private readonly SHOOT_DELAY: number = 0.2; // Can shoot every 200ms
  private readonly BULLET_SPEED: number = 500;

  constructor(
    id: string,
    x: number,
    y: number,
    renderManager: RenderManager,
    audioManager: AudioManager,
    gameWidth?: number,
    gameHeight?: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.velocity = { x: 0, y: 0 };
    this.size = { x: 16, y: 16 };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.gameWidth = gameWidth || 800;
    this.gameHeight = gameHeight || 600;
  }

  public update(deltaTime: number): void {
    // Update shoot cooldown
    this.shootCooldown -= deltaTime;

    // Apply friction
    this.velocity.x *= this.FRICTION;
    this.velocity.y *= this.FRICTION;

    // Clamp velocity to max speed
    const speed = Math.sqrt(this.velocity.x * this.velocity.x + this.velocity.y * this.velocity.y);
    if (speed > this.MAX_SPEED) {
      const ratio = this.MAX_SPEED / speed;
      this.velocity.x *= ratio;
      this.velocity.y *= ratio;
    }

    // Update position
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;

    // Wraparound screen edges
    this.wrapAroundScreen();

    // Reset thrusting flag (will be set again if thrust key is pressed)
    this.isThrusting = false;
  }

  private wrapAroundScreen(): void {
    if (this.position.x < -this.size.x) {
      this.position.x = this.gameWidth;
    } else if (this.position.x > this.gameWidth) {
      this.position.x = -this.size.x;
    }

    if (this.position.y < -this.size.y) {
      this.position.y = this.gameHeight;
    } else if (this.position.y > this.gameHeight) {
      this.position.y = -this.size.y;
    }
  }

  public rotateLeft(): void {
    this.angle -= this.ROTATION_SPEED * (1/60); // Assume 60fps for smooth rotation
  }

  public rotateRight(): void {
    this.angle += this.ROTATION_SPEED * (1/60);
  }

  public thrust(): void {
    this.isThrusting = true;
    
    // Convert angle to radians and apply thrust in facing direction
    const angleRad = (this.angle - 90) * (Math.PI / 180); // -90 to face up initially
    const thrustX = Math.cos(angleRad) * this.THRUST_POWER * (1/60);
    const thrustY = Math.sin(angleRad) * this.THRUST_POWER * (1/60);
    
    this.velocity.x += thrustX;
    this.velocity.y += thrustY;
  }

  public shoot(): Bullet | null {
    if (this.shootCooldown > 0) return null;

    this.shootCooldown = this.SHOOT_DELAY;
    this.audioManager.playShoot();

    // Calculate bullet spawn position at ship's nose
    const angleRad = (this.angle - 90) * (Math.PI / 180);
    const spawnDistance = 12; // Distance from ship center to nose
    const bulletX = this.position.x + this.size.x / 2 + Math.cos(angleRad) * spawnDistance;
    const bulletY = this.position.y + this.size.y / 2 + Math.sin(angleRad) * spawnDistance;

    // Calculate bullet velocity (ship velocity + bullet speed in facing direction)
    const bulletVelX = this.velocity.x + Math.cos(angleRad) * this.BULLET_SPEED;
    const bulletVelY = this.velocity.y + Math.sin(angleRad) * this.BULLET_SPEED;

    return new Bullet(
      `bullet_${Date.now()}`,
      bulletX,
      bulletY,
      bulletVelX,
      bulletVelY,
      false, // Player bullet
      this.renderManager,
      this.audioManager,
      this.gameWidth,
      this.gameHeight
    );
  }

  public hyperspace(newX: number, newY: number): void {
    this.position.x = newX;
    this.position.y = newY;
    this.velocity.x = 0;
    this.velocity.y = 0;
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    const centerX = this.position.x + this.size.x / 2;
    const centerY = this.position.y + this.size.y / 2;

    // Ship triangle points (pointing up initially)
    const shipPoints = [
      { x: 0, y: -10 },  // Nose
      { x: -6, y: 8 },   // Left wing
      { x: -3, y: 5 },   // Left inner
      { x: 3, y: 5 },    // Right inner  
      { x: 6, y: 8 }     // Right wing
    ];

    // Rotate points based on ship angle
    const angleRad = this.angle * (Math.PI / 180);
    const rotatedPoints = shipPoints.map(point => {
      const rotX = point.x * Math.cos(angleRad) - point.y * Math.sin(angleRad);
      const rotY = point.x * Math.sin(angleRad) + point.y * Math.cos(angleRad);
      return { x: centerX + rotX, y: centerY + rotY };
    });

    // Draw ship outline
    this.renderManager.drawPolygon(rotatedPoints, '#FFFFFF', false);

    // Draw thrust flame if thrusting
    if (this.isThrusting) {
      const thrustPoints = [
        { x: -3, y: 5 },
        { x: 0, y: 15 },
        { x: 3, y: 5 }
      ];

      const rotatedThrustPoints = thrustPoints.map(point => {
        const rotX = point.x * Math.cos(angleRad) - point.y * Math.sin(angleRad);
        const rotY = point.x * Math.sin(angleRad) + point.y * Math.cos(angleRad);
        return { x: centerX + rotX, y: centerY + rotY };
      });

      this.renderManager.drawPolygon(rotatedThrustPoints, '#FF8800', false);
    }
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public destroy(): void {
    this.active = false;
  }

  public reset(): void {
    this.active = true;
    this.velocity.x = 0;
    this.velocity.y = 0;
    this.angle = 0;
    this.shootCooldown = 0;
    this.isThrusting = false;
  }
}